// Global Instructions Rule Applied!
// Shapes of the fund Investors-tab commitment editor reads/writes:
//   GET  /funds/:id/commitment-options  — view__fund_commitment_editor_options
//   POST /funds/commitments             — fund__save_commitment result
// Ids are plain z.string() (legacy Dataverse ids preclude .uuid()).
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

const StringOptionSchema = z.object({ value: z.string(), label: z.string() }).strict()
const NumberOptionSchema = z.object({ value: z.number(), label: z.string() }).strict()

export const FundCommitmentEditorOptionsSchema = z
  .object({
    fund: z
      .object({
        funds__id: z.string(),
        funds__name: z.string().nullable(),
        currencies__id: z.string().nullable(),
        currencies__code: z.string().nullable(),
        currencies__symbol: z.string().nullable(),
        funds__mgmt_fee_rate: z.number().nullable(),
        // the fund's default carry terms (a blank commitment term inherits them)
        funds__base_carry_rate: z.number().nullish(),
        funds__base_hurdle_multiple: z.number().nullish(),
        funds__stepped_carry_rate: z.number().nullish(),
        funds__stepped_hurdle_multiple: z.number().nullish(),
        // SBIC fund: the investor editors show the SBIC investor type
        funds__is_sbic: z.boolean().nullish(),
        // Non-null = consolidated-fund member (commitments added there).
        fund_groups__name: z.string().nullable()
      })
      .strict()
      .nullable(),
    investors: z.array(StringOptionSchema.extend({ accounts__id: z.string() }).strict()),
    statuses: z.array(NumberOptionSchema),
    classifications: z.array(NumberOptionSchema),
    // cfg.sbic_investor_types (SBA capital treatment); defaulted for older backends
    sbic_investor_types: z.array(NumberOptionSchema).optional().default([]),
    // cfg.notice_templates supplements (the ILPA workbook) a commitment's
    // capital call notices can carry; defaulted for older backends
    notice_supplements: z.array(NumberOptionSchema).optional().default([])
  })
  .strict()

export const FundCommitmentSaveResultSchema = z.union([
  z
    .object({
      status: z.literal('ok'),
      fund_commitments__id: z.string(),
      investors__id: z.string()
    })
    .strict(),
  z
    .object({
      status: z.literal('below_called_warning'),
      prog__new_amount: z.number(),
      prog__called_to_date: z.number()
    })
    .strict()
])

export type FundCommitmentEditorOptions = z.infer<typeof FundCommitmentEditorOptionsSchema>
export type FundCommitmentSaveResult = z.infer<typeof FundCommitmentSaveResultSchema>

export const validateFundCommitmentEditorOptions = (data: unknown): FundCommitmentEditorOptions =>
  validateWithZodSchema(FundCommitmentEditorOptionsSchema, data, 'fund commitment editor options')

export const validateFundCommitmentSaveResult = (data: unknown): FundCommitmentSaveResult =>
  validateWithZodSchema(FundCommitmentSaveResultSchema, data, 'fund commitment save result')
